import type { PromptGroup } from '@/types/database-schema';

export interface PromptTopicFilter {
  include: string[];
  exclude: string[];
}

export interface AppliedPromptTopicFilter extends PromptTopicFilter {
  promptIds: string[];
}

export const EMPTY_TOPIC_FILTER: PromptTopicFilter = { include: [], exclude: [] };

export function promptTopicFilterNeedsInventory(filter?: PromptTopicFilter): boolean {
  return Boolean(
    filter &&
      (!filter.include.length ||
        [...filter.include, ...filter.exclude].some(value => {
          const ref = parseTopicFilterValue(value);
          return ref?.promptId && !ref.topicId && !ref.subtopicId;
        }))
  );
}

export function topicFilterValue(topicId: string, subtopicId = '', promptId = ''): string {
  if (promptId) return `prompt:${JSON.stringify([topicId, promptId, subtopicId])}`;
  if (subtopicId) return `subtopic:${JSON.stringify([topicId, subtopicId])}`;
  return topicId ? `topic:${topicId}` : '';
}

export function parseTopicFilterValue(value: string) {
  try {
    if (value.startsWith('topic:') && value.length > 6)
      return { topicId: value.slice(6), subtopicId: '', promptId: '' };
    const child = value.startsWith('subtopic:');
    if (!child && !value.startsWith('prompt:')) return null;
    const ids: unknown = JSON.parse(value.slice(child ? 9 : 7));
    if (!Array.isArray(ids) || ids.some(id => typeof id !== 'string')) return null;
    if (child && ids.length === 2 && ids.every(Boolean))
      return { topicId: ids[0] as string, subtopicId: ids[1] as string, promptId: '' };
    if (!child && ids.length === 3 && ids[1])
      return {
        topicId: ids[0] as string,
        promptId: ids[1] as string,
        subtopicId: ids[2] as string,
      };
  } catch {
    return null;
  }
  return null;
}

export function parsePromptTopicFilter(raw: string | null): PromptTopicFilter | undefined {
  if (!raw || raw.length > 200_000) return undefined;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object') return undefined;
    for (const key of ['include', 'exclude']) {
      if (
        !Array.isArray(value[key]) ||
        value[key].length > 2000 ||
        value[key].some(
          (entry: unknown) =>
            typeof entry !== 'string' || entry.length > 512 || !parseTopicFilterValue(entry)
        )
      )
        return undefined;
    }
    return {
      include: [...new Set<string>(value.include)].sort(),
      exclude: [...new Set<string>(value.exclude)].sort(),
    };
  } catch {
    return undefined;
  }
}

/** Union included memberships, then subtract exclusions globally, including overlaps. */
export function resolvePromptTopicFilter(
  filter: PromptTopicFilter,
  topics: PromptGroup[],
  inventoryIds: string[] = []
) {
  const universe = new Set([...inventoryIds, ...topics.flatMap(topic => topic.prompt_ids)]);
  let valid = true;
  const members = (value: string): string[] => {
    const ref = parseTopicFilterValue(value);
    const topic = topics.find(topic => topic.id === ref?.topicId);
    const child = topic?.subtopics?.find(child => child.id === ref?.subtopicId);
    if (
      !ref ||
      (ref.topicId && !topic) ||
      (ref.subtopicId && !child) ||
      (ref.promptId &&
        (!universe.has(ref.promptId) ||
          (topic && !topic.prompt_ids.includes(ref.promptId)) ||
          (child && !child.prompt_ids.includes(ref.promptId))))
    ) {
      valid = false;
      return [];
    }
    return ref.promptId ? [ref.promptId] : child?.prompt_ids || topic?.prompt_ids || [];
  };
  const included = new Set(filter.include.length ? filter.include.flatMap(members) : universe);
  const excluded = new Set(filter.exclude.flatMap(members));
  return {
    valid,
    promptIds: valid ? [...included].filter(id => !excluded.has(id)).sort() : [],
  };
}

export function topicFilterBase(filter: PromptTopicFilter) {
  return filter.include.length === 1
    ? parseTopicFilterValue(filter.include[0])
    : { topicId: '', subtopicId: '', promptId: '' };
}
